import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getNormalQuestionaireFormPreview, submitNormalQuestionaireForm } from '../../../../services/CommonService'
import { toast } from 'react-toastify'
import Utils from '../../../../utils'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

function SubmitNormalForm() {
  const navigate = useNavigate()
  const { form_uuid } = useParams()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState(null)
  const [answers, setAnswers] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const datePickerRefs = useRef({})


  const handleBack = () => {
    navigate('/client-portal/questionnaire/manage')
  }

  useEffect(() => {
    if (form_uuid) {
      fetchFormQuestions()
    }
  }, [form_uuid])

  const fetchFormQuestions = async () => {
    try {
      setLoading(true)
      const response = await getNormalQuestionaireFormPreview({ form_uuid: form_uuid })
      if (response.data && response.data.data) {
        setFormData(response.data.data)
      } else {
        toast.error('Failed to load form questions')
      }
    } catch (error) {
      toast.error(Utils.getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const handleAnswerChange = (questionId, value, questionType = 'text') => {
    // Handle date values - convert Date object to YYYY-MM-DD string format
    if (questionType === 'date' && value instanceof Date) {
      const formattedDate = value.toISOString().split('T')[0]
      setAnswers(prev => ({
        ...prev,
        [questionId]: formattedDate
      }))
    } else {
      setAnswers(prev => ({
        ...prev,
        [questionId]: value
      }))
    }
  }

  const handleCheckboxChange = (questionId, optionId, checked) => {
    setAnswers(prev => {
      const currentAnswers = prev[questionId] || []
      if (checked) {
        return {
          ...prev,
          [questionId]: [...currentAnswers, optionId]
        }
      } else {
        return {
          ...prev,
          [questionId]: currentAnswers.filter(val => val !== optionId)
        }
      }
    })
  }

  const validateForm = () => {
    if (!formData || !formData.groups) return false

    for (const group of formData.groups) {
      for (const question of group.questions || []) {
        if (question.is_required) {
          const answer = answers[question.question_id]
          if (!answer ||
            (Array.isArray(answer) && answer.length === 0) ||
            (typeof answer === 'string' && answer.trim() === '')) {
            toast.error(`Please answer the required question: ${question.question_text}`)
            return false
          }
        }
      }
    }
    return true
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    try {
      setSubmitting(true)
      setLoading(true)
      // Convert answers to the required format
      const answersArray = Object.entries(answers).map(([questionId, answer]) => {
        // Find the question to determine its type
        const question = formData?.groups?.flatMap(group => group.questions || [])
          ?.find(q => q.question_id === parseInt(questionId))

        if (Array.isArray(answer)) {
          // For checkbox answers (array of option IDs)
          return {
            question_id: parseInt(questionId),
            answer_option_ids: answer // Array of option IDs for checkboxes
          }
        } else if (question && question.question_type === 'radio') {
          // For radio button answers (single option ID)
          return {
            question_id: parseInt(questionId),
            answer_option_id: answer // Single option ID for radio
          }
        } else {
          // For text, number, email, date, textarea questions
          return {
            question_id: parseInt(questionId),
            answer_text: String(answer)
          }
        }
      })

      const payload = {
        form_uuid: form_uuid,
        answers: answersArray
      }

      const response = await submitNormalQuestionaireForm(payload)

      if (response.data) {
        toast.success('Form submitted successfully!')
        navigate('/client-portal/questionnaire/manage')
      } else {
        toast.error('Failed to submit form')
      }
    } catch (error) {
      toast.error(Utils.getErrorMessage(error))
    } finally {
      setSubmitting(false)
      setLoading(false)
    }
  }

  return (
    <>
      {loading && <div className="page-loading">
        <img src="/images/loader.gif" alt="loader" />
      </div>}
      <div className="section-bg-color" style={{ minHeight: "calc(100vh - 100px0)", marginTop: "60px" }}>
        <div className="container py-4">
          {/* Header Section */}
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h2 className="mb-0">Submit Normal Form</h2>
            <button
              className="modern-back-btn me-3 d-flex align-items-center bg-black text-white"
              onClick={handleBack}
              type="button"
            >
              <span className="back-arrow">&larr;</span>
              <span className="back-text">Back</span>
            </button>
          </div>

          {!loading && formData && (
            <div className="card">
              <div className="card-header">
                <h4 className="mb-0">
                  {formData.form_name || 'Customer Demand & Details Form'}
                </h4>
              </div>

              <div className="card-body">
                {formData.groups && formData.groups.length > 0 ? (
                  formData.groups.map((group, groupIndex) => (
                    <div key={group.group_id} className="mb-4">
                      {/* Group Header */}
                      <div className="border p-3 mb-3">
                        <h5 className="mb-3">{group.group_name}</h5>

                        {/* Group Content */}
                        <div>
                          {group.questions && group.questions.length > 0 ? (
                            group.questions.map((question, questionIndex) => (
                              <div key={question.question_id} className="mb-4 p-3 border">
                                <label className="form-label fw-bold">
                                  {question.question_text}
                                  {question.is_required && (
                                    <span className="text-danger ms-1">*</span>
                                  )}
                                </label>

                                {/* Text Input */}
                                {question.question_type === 'text' && (
                                  <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Enter your answer..."
                                    value={answers[question.question_id] || ''}
                                    onChange={(e) => handleAnswerChange(question.question_id, e.target.value)}
                                  />
                                )}

                                {/* Number Input */}
                                {question.question_type === 'number' && (
                                  <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Enter number..."
                                    value={answers[question.question_id] || ''}
                                    onChange={(e) => handleAnswerChange(question.question_id, e.target.value)}
                                  />
                                )}

                                {/* Email Input */}
                                {question.question_type === 'email' && (
                                  <input
                                    type="email"
                                    className="form-control"
                                    placeholder="Enter email address..."
                                    value={answers[question.question_id] || ''}
                                    onChange={(e) => handleAnswerChange(question.question_id, e.target.value)}
                                  />
                                )}

                                {/* Date Input */}
                                {question.question_type === 'date' && (
                                  <div className="position-relative">
                                    <DatePicker
                                      ref={(el) => (datePickerRefs.current[question.question_id] = el)}
                                      selected={answers[question.question_id] ? new Date(answers[question.question_id]) : null}
                                      onChange={(date) => handleAnswerChange(question.question_id, date, 'date')}
                                      className="form-control"
                                      placeholderText="dd-mm-yyyy"
                                      dateFormat="dd/MM/yyyy"
                                      showPopperArrow={false}
                                      isClearable={false}
                                      // showYearDropdown
                                      // showMonthDropdown    
                                      dropdownMode="select"
                                      shouldCloseOnSelect={true}
                                      autoComplete="off"
                                      openToDate={new Date()}
                                    />
                                    <i
                                      className="bi bi-calendar"
                                      style={{
                                        position: "absolute",
                                        right: "16px",
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        cursor: "pointer",
                                        color: "#888",
                                        fontSize: "16px"
                                      }}
                                      onClick={() => datePickerRefs.current[question.question_id]?.setFocus(true)}
                                    />
                                  </div>
                                )}

                                {/* Textarea */}
                                {question.question_type === 'textarea' && (
                                  <textarea
                                    className="form-control"
                                    rows="4"
                                    placeholder="Enter your answer..."
                                    value={answers[question.question_id] || ''}
                                    onChange={(e) => handleAnswerChange(question.question_id, e.target.value)}
                                  ></textarea>
                                )}

                                {/* Radio Options */}
                                {question.question_type === 'radio' && question.options && question.options.length > 0 && (
                                  <div>
                                    {question.options.map((option, optionIndex) => (
                                      <div key={optionIndex} className="form-check mb-2">
                                        <input
                                          className="form-check-input"
                                          type="radio"
                                          name={`question_${question.question_id}`}
                                          id={`question_${question.question_id}_option_${optionIndex}`}
                                          value={option.id || option.option_id}
                                          checked={answers[question.question_id] === (option.id || option.option_id)}
                                          onChange={(e) => handleAnswerChange(question.question_id, parseInt(e.target.value))}
                                        />
                                        <p
                                          className="form-check-label text-black"
                                          htmlFor={`question_${question.question_id}_option_${optionIndex}`}
                                        >
                                          {option.name}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Checkbox Options */}
                                {question.question_type === 'checkbox' && question.options && question.options.length > 0 && (
                                  <div>
                                    {question.options.map((option, optionIndex) => (
                                      <div key={optionIndex} className="form-check mb-2">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          id={`question_${question.question_id}_option_${optionIndex}`}
                                          value={option.id || option.option_id}
                                          checked={(answers[question.question_id] || []).includes(option.id || option.option_id)}
                                          onChange={(e) => handleCheckboxChange(question.question_id, parseInt(e.target.value), e.target.checked)}
                                        />
                                        <label
                                          className="form-check-label"
                                          htmlFor={`question_${question.question_id}_option_${optionIndex}`}
                                        >
                                          {option.name}
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))
                          ) : (
                            <div className="alert alert-info">
                              No questions in this group.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="alert alert-warning">
                    No groups found in this form.
                  </div>
                )}

                {/* Submit Section */}
                <div className="mt-4 pt-3 border-top">
                  <div className="d-flex justify-content-center gap-3">
                    <button
                      className="btn btn-save"
                      onClick={handleSubmit}
                      disabled={submitting}
                    >
                      Submit
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={handleBack}
                      disabled={submitting}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!loading && !formData && (
            <div className="alert alert-warning text-center">
              <h5 className="mb-0">No form data available</h5>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default SubmitNormalForm